import { useEffect } from "react";

import { useUserData } from "src/store/AuthStore";

const THENA_AGENT_ID = "0be44f65-4b1c-42e8-a564-fc3a7630965e";
const THENA_BASE_URL = "https://agent-studio.thena.ai";
const THENA_WS_ENDPOINT = "wss://agent-studio.thena.ai";
const TAKTILE_LOGO_URL =
  "https://25449300.fs1.hubspotusercontent-eu1.net/hubfs/25449300/logo.png";

type ThenaConfig = {
  baseUrl: string;
  apiKey: string;
  agentId: string;
  wsEndpoint: string;
  useCustomLauncher: boolean;
  user: {
    email: string;
    name?: string;
    hash: string;
  };
  initialPosition: {
    top: string;
    left: string;
    bottom: string;
    right: string;
  };
  themeColorStart: string;
  brandLogoUrl: string;
  notifications: {
    enabled: boolean;
    sound: boolean;
  };
};

type ThenaWindow = Window & {
  thenaKey?: string;
  thenaWidget?: ThenaConfig;
  thena?: {
    open: () => void;
  };
};

const injectThenaScript = () => {
  if (!document || document.getElementById("thena-script")) {
    return;
  }

  const script = document.createElement("script");
  script.id = "thena-script";
  script.src = "https://widget.thena.ai/shim.js";
  document.body.appendChild(script);
};

const buildThenaWidgetConfig = (
  email: string,
  name: string,
  hashedEmail: string,
): ThenaConfig => {
  const thenaWindow = window as ThenaWindow;
  const thenaKey = thenaWindow.thenaKey;
  if (!thenaKey) {
    throw new Error("Thena key is not set");
  }

  return {
    baseUrl: THENA_BASE_URL,
    apiKey: thenaKey,
    agentId: THENA_AGENT_ID,
    wsEndpoint: THENA_WS_ENDPOINT,
    useCustomLauncher: false,
    user: {
      email,
      name,
      hash: hashedEmail,
    },
    initialPosition: {
      top: "auto",
      left: "20px",
      bottom: "20px",
      right: "20px",
    },
    themeColorStart: "#493FC1",
    brandLogoUrl: TAKTILE_LOGO_URL,
    notifications: {
      enabled: true,
      sound: false,
    },
  };
};

type Args = {
  enabled: boolean;
};

export const useBootTheena = ({ enabled }: Args) => {
  const {
    signed_in_email,
    signed_in_user_id,
    signed_in_full_name,
    signed_in_username,
    hashed_email,
  } = useUserData();

  // Boot the Thena client when the user has signed in
  useEffect(() => {
    // Don't boot the Thena client in Cypress or if the its not enabled
    if ("Cypress" in window || !enabled) {
      return;
    }

    if (hashed_email) {
      const email =
        signed_in_email ||
        signed_in_username ||
        `user+${signed_in_user_id}@taktile.com`;
      const name =
        signed_in_full_name ||
        signed_in_username?.split("@")?.[0] ||
        `Taktile User ${signed_in_user_id}`;

      setTimeout(() => {
        (window as ThenaWindow).thenaWidget = buildThenaWidgetConfig(
          email,
          name,
          hashed_email,
        );
        injectThenaScript();
      }, 2000);
    }
  }, [
    signed_in_full_name,
    signed_in_user_id,
    signed_in_email,
    signed_in_username,
    hashed_email,
    enabled,
  ]);
};

export const toggleThenaWidget = () => {
  const thenaWindow = window as ThenaWindow;
  if (thenaWindow.thena) {
    thenaWindow.thena.open();
  }
};
